import { useEffect, useRef, useState } from 'react';

import { PostGeoConfigLocation } from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import Popover from '@lotlinx-aws/sparky-component-library/Sparky/Popover';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';

import styles from './GeoMapPinPopup.module.scss';

export interface GeoMapPinPopupProps {
  anchorElement: Element | null;
  location: PostGeoConfigLocation;
  editing: boolean;
}

/**
 * GeoMapPinPopup
 */
export default function GeoMapPinPopup({ anchorElement, location, editing }: GeoMapPinPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location) {
      const timer = setTimeout(() => setIsOpen(true), 800);
      return () => clearTimeout(timer);
    } else {
      setIsOpen(false);
      return location;
    }
  }, [location]);

  useEffect(() => {
    if (anchorElement && wrapperRef.current) {
      const updatePosition = () => {
        const rect = anchorElement.getBoundingClientRect();
        if (wrapperRef.current) {
          wrapperRef.current.style.position = 'fixed';
          wrapperRef.current.style.left = `${rect.left + rect.width / 2}px`;
          wrapperRef.current.style.top = `${rect.top + rect.height / 2}px`;
          wrapperRef.current.style.width = '0';
          wrapperRef.current.style.height = '0';
          wrapperRef.current.style.pointerEvents = 'none';
        }
      };

      updatePosition();
      const interval = setInterval(updatePosition, 100);

      return () => clearInterval(interval);
    }
  }, [anchorElement]);

  if (!anchorElement) {
    return null;
  }

  return (
    <div ref={wrapperRef}>
      <Popover
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        side='bottom'
        verticalOffset={2}
        showCloseButton={false}
        isHoverTriggered={false}
        isClickTriggered={false}
        contentClassName={styles.geoMapPinPopup}
        popupContent={
          <div className={styles.popupContainer}>
            <Text size='16'>{location.name}</Text>
            <div className={styles.popupContent}>
              <div className={styles.content}>
                <Text color='secondary'>Latitude</Text>
                <Text color='secondary'>{location.latitude}</Text>
              </div>
              <div className={styles.content}>
                <Text color='secondary'>Longitude</Text>
                <Text color='secondary'>{location.longitude}</Text>
              </div>
              <div className={styles.content}>
                <Text color='secondary'>Radius</Text>
                <Text color='secondary'>{location.radius}</Text>
              </div>
              {editing && (
                <Message variant='muted' category='info'>
                  Click and drag pin to edit its position or edit the latitude & longitude values in
                  the left panel.
                </Message>
              )}
            </div>
          </div>
        }
      >
        <div style={{ width: 0, height: 0 }} />
      </Popover>
    </div>
  );
}
